import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ProviderForm } from "../components/paymentProviders/ProviderForm";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Pill } from "../components/ui/Status";
import { Cell, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric } from "../components/ui/Text";
import { get, post } from "../lib/api";
import { count } from "../lib/format";
import { credentialStatus } from "../lib/paymentProviders";
import { useSession } from "../lib/session";
import type { PaymentProvider } from "../lib/types";

/**
 * Payment providers — the merchant accounts drivers' money is taken through
 * (server docs/payment-providers-plan.md).
 *
 * Each depot is served by one provider; one provider is the default a new depot
 * starts with. An order keeps the provider it was paid through for life, so its
 * refund goes back to the same merchant even after its depot moves.
 *
 * Credentials are write-only. The server stores them encrypted and returns only
 * whether each is set, when, and by whom — this page could not show a key if it
 * wanted to.
 */

const COLUMNS: readonly Column[] = [
  { key: "label", label: "Provider", width: "20%" },
  { key: "environment", label: "Environment", width: "10%" },
  { key: "merchant", label: "Merchant", width: "11%" },
  { key: "apiKey", label: "API key", width: "18%" },
  { key: "refundKey", label: "RSA key", width: "12%" },
  { key: "depots", label: "Depots", numeric: true, width: "7%" },
  { key: "orders", label: "Orders", numeric: true, width: "7%" },
];

/** Appended only for an operator who may manage. Empty label: the buttons name themselves. */
const ACTIONS_COLUMN: Column = { key: "actions", label: "", width: "15%" };

/** Times in the operator's own zone: a key rotation is "when did I do that", not a report. */
const ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function PaymentProviders() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const providers = useQuery({
    queryKey: ["payment-providers"],
    queryFn: () => get<PaymentProvider[]>("/payment-providers"),
  });

  const makeDefault = useMutation({
    mutationFn: (id: string) =>
      post<PaymentProvider | null>(`/payment-providers/${encodeURIComponent(id)}/default`),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ["payment-providers"] });
      setNotice(
        `${saved?.label ?? "Provider"} is the default. New depots get it; existing depots keep theirs.`,
      );
    },
  });

  const rows = providers.data ?? [];
  const canManage = can("payment_providers.manage");
  const columns = [...COLUMNS, ...(canManage ? [ACTIONS_COLUMN] : [])];
  const editing = rows.find((row) => row.id === editingId);
  const withoutRefundKey = rows.filter((row) => !row.refundKey.set).length;
  const depotsServed = rows.reduce((sum, row) => sum + row.depots.length, 0);
  const live = rows.filter((row) => row.environment === "production").length;

  function done(message: string) {
    setNotice(message);
    setIsCreating(false);
    setEditingId(undefined);
  }

  const createButton =
    canManage && !isCreating ? (
      <Button
        variant="primary"
        onClick={() => {
          setEditingId(undefined);
          setNotice(undefined);
          setIsCreating(true);
        }}
      >
        New provider
      </Button>
    ) : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Payment providers"
        metrics={
          <>
            <Metric label="Providers" value={count(rows.length)} hint={`${count(live)} live`} />
            <Metric label="Depots served" value={count(depotsServed)} />
            <Metric
              label="Without RSA key"
              value={count(withoutRefundKey)}
              hint="refunds cleared by hand"
              tone={withoutRefundKey > 0 ? "signal" : "muted"}
            />
          </>
        }
        lede="The merchant accounts drivers pay through. Each depot is served by one; an order refunds through the provider it was paid with, even after its depot moves. Keys are stored encrypted and can be replaced, never read back."
        actions={createButton}
      />

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {makeDefault.isError ? <ErrorNotice error={makeDefault.error} /> : null}

      {isCreating ? (
        <ProviderForm timeZone={ZONE} onDone={done} onCancel={() => setIsCreating(false)} />
      ) : null}

      {editing === undefined ? null : (
        <ProviderForm
          key={editing.id}
          provider={editing}
          timeZone={ZONE}
          onDone={done}
          onCancel={() => setEditingId(undefined)}
        />
      )}

      {providers.isError ? (
        <ErrorNotice error={providers.error} onRetry={() => void providers.refetch()} />
      ) : null}

      <TableFrame>
        <TableHead columns={columns} />
        {providers.isPending ? (
          <SkeletonRows rows={2} columns={columns.length} />
        ) : (
          <tbody>
            {rows.map((row, index) => (
              <TableRow key={row.id} index={index}>
                <Cell>
                  <span className="font-medium text-ink">{row.label}</span>{" "}
                  {row.isDefault ? <Pill tone="available">Default</Pill> : null}
                </Cell>
                <Cell>
                  {row.environment === "production" ? (
                    <Pill tone="charging">Production</Pill>
                  ) : (
                    <Pill>Sandbox</Pill>
                  )}
                </Cell>
                <Cell className="font-mono text-micro">{row.merchantId}</Cell>
                <Cell muted className="text-micro tracking-normal normal-case">
                  {credentialStatus(row.apiKey, ZONE)}
                </Cell>
                <Cell muted={!row.refundKey.set} className="text-micro tracking-normal normal-case">
                  {row.refundKey.set ? "Set" : "Not set — refunds by hand"}
                </Cell>
                <Cell numeric muted={row.depots.length === 0}>
                  <span title={row.depots.map((depot) => depot.name).join(", ")}>
                    {count(row.depots.length)}
                  </span>
                </Cell>
                <Cell numeric muted={row.orderCount === 0}>
                  {count(row.orderCount)}
                </Cell>
                {canManage ? (
                  <Cell>
                    <span className="flex justify-end gap-2 whitespace-nowrap">
                      {row.isDefault ? null : (
                        <Button
                          variant="ghost"
                          aria-label={`Make ${row.label} the default`}
                          pending={makeDefault.isPending && makeDefault.variables === row.id}
                          onClick={() => makeDefault.mutate(row.id)}
                        >
                          Make default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        aria-label={`Edit ${row.label}`}
                        onClick={() => {
                          setIsCreating(false);
                          setNotice(undefined);
                          setEditingId(row.id);
                        }}
                      >
                        Edit
                      </Button>
                    </span>
                  </Cell>
                ) : null}
              </TableRow>
            ))}
          </tbody>
        )}
      </TableFrame>

      {!providers.isPending && !providers.isError && rows.length === 0 ? (
        <EmptyState
          title="No payment providers yet."
          detail="Until one exists, no depot can take a payment: drivers see stations, but cannot start a paid charge. Add the merchant account ABA issued, and make it the default."
          action={createButton}
        />
      ) : null}
    </>
  );
}
